import { Spacing } from '@persona-id/design-tokens';
import { isEmpty } from 'lodash/fp';
import { Fragment } from 'react';
import { FieldArray, FormSection, WrappedFieldArrayProps } from 'redux-form';
import styled, { css } from 'styled-components';

import ReactSelectField from 'dashboard/components/FormControls/ReactSelectField';
import Card from 'design-system/components/Card';
import Sticker from 'design-system/components/Sticker';
import { Label, Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import Field from 'lib/interfaces/FormControlField';
import { SelfieVideoCaptureMethod } from 'lib/interfaces/verificationConfig';

const CAPTURE_FILE_TYPE_LABELS = {
  video: {
    title: 'Enable video capture',
    description: 'Capture video during the verification process',
  },
};

const VIDEO_CAPTURE_METHODS = [
  `${SelfieVideoCaptureMethod.Stream}-${SelfieVideoCaptureMethod.Upload}`,
  `${SelfieVideoCaptureMethod.Upload}-${SelfieVideoCaptureMethod.Stream}`,
  SelfieVideoCaptureMethod.Stream,
  SelfieVideoCaptureMethod.Upload,
];
type VideoCaptureMethod = (typeof VIDEO_CAPTURE_METHODS)[number];

const VIDEO_CAPTURE_METHODS_OPTIONS: { label: string; value: VideoCaptureMethod }[] = [
  {
    label: 'Stream, fallback to upload',
    value: VIDEO_CAPTURE_METHODS[0],
  },
  {
    label: 'Upload, fallback to stream',
    value: VIDEO_CAPTURE_METHODS[1],
  },
  {
    label: 'Stream only',
    value: VIDEO_CAPTURE_METHODS[2],
  },
  {
    label: 'Upload only',
    value: VIDEO_CAPTURE_METHODS[3],
  },
];
interface FormSectionVideoCaptureConfigProps {
  name: string;
  enabledCaptureFileTypes?: string[];
  disabled: boolean;
}

const FormSectionVideoCaptureConfig = ({
  name,
  enabledCaptureFileTypes,
  disabled,
}: FormSectionVideoCaptureConfigProps) => {
  return (
    <FormSection name={name} component={Fragment}>
      <Card>
        <Card.Body>
          <Card.Title>
            Video capture <Sticker variant='yellow'>BETA</Sticker>
          </Card.Title>
          <FieldArray
            key='enabled-capture-file-types'
            name='enabled-capture-file-types'
            component={ToggleRowFields}
            options={['video']}
            labels={CAPTURE_FILE_TYPE_LABELS}
            disabled={disabled}
          />
          <ToggleRowWrapper last={true}>
            <ToggleLabelContainer>
              <Label size='md' weight='bold' htmlFor={`${name}.video-capture-methods-input`}>
                Capture methods
              </Label>
              <Paragraph>
                Configure what methods of video capture should be prioritized. &quot;Stream&quot;
                will livestream the video to Persona; &quot;Upload&quot; will record the video on
                the client and upload at the end of the verification.
              </Paragraph>
            </ToggleLabelContainer>
            <ToggleContainer>
              <Field
                name='video-capture-methods'
                id={`${name}.video-capture-methods`}
                component={ReactSelectField}
                parse={(value: string) => (value ? value.split('-') : value)}
                format={(value: string[]) => (value ? value.join('-') : '')}
                options={VIDEO_CAPTURE_METHODS_OPTIONS}
                disabled={
                  disabled ||
                  isEmpty(enabledCaptureFileTypes) ||
                  !(enabledCaptureFileTypes ?? []).includes('video')
                }
              />
            </ToggleContainer>
          </ToggleRowWrapper>
        </Card.Body>
      </Card>
    </FormSection>
  );
};

const ToggleRowFields = (
  props: WrappedFieldArrayProps & {
    options: string[];
    labels: ToggleLabels;
    disabled: boolean;
  },
) => {
  const { fields, options, labels, disabled, meta, ...passthroughProps } = props;

  return (
    <>
      {options.map((option, idx) => {
        const title = labels[option].title;
        const description = labels[option].description;
        const currentFields = fields.getAll();
        const value = (currentFields || []).includes(option);
        const captureOptionDisabled = disabled;

        return (
          <ToggleRowWrapper key={idx} last={false}>
            <ToggleLabelContainer>
              <Label size='md' weight='bold'>
                {title}
              </Label>
              <Paragraph>{description}</Paragraph>
            </ToggleLabelContainer>
            <ToggleContainer>
              <span>
                <ToggleSlider
                  data-test={`toggle-row-${fields.name}-${idx}__slider`}
                  value={value}
                  {...passthroughProps}
                  onChange={(enabled) => {
                    if (enabled) {
                      fields.push(option);
                    } else {
                      const index = currentFields.indexOf(option, 0);
                      if (index > -1) {
                        fields.remove(index);
                      }
                    }
                  }}
                  disabled={captureOptionDisabled}
                  labelOff='Off'
                  labelOn='On'
                />
              </span>
            </ToggleContainer>
          </ToggleRowWrapper>
        );
      })}
      {meta.invalid && <ErrorMessage>{meta.error}</ErrorMessage>}
    </>
  );
};

type ToggleLabels = Record<string, { title?: string; description: string }>;

const ToggleRowWrapper = styled('div')<{ last: boolean }>`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing[4]} 0;
  border-top: 1px solid ${(props) => props.theme.color.border.secondary};

  ${(props) =>
    props.last &&
    css`
      border-bottom: 1px solid ${(props) => props.theme.color.border.secondary};
    `}
`;

const ToggleLabelContainer = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-right: auto;
  align-self: flex-start;
  justify-content: space-between;
  max-width: 50%;
`;

const ToggleContainer = styled('div')`
  height: 100%;
  margin-left: auto;
  margin-bottom: auto;
  align-self: flex-end;
`;

const ErrorMessage = styled('div')`
  color: ${({ theme }) => theme.color.variants.danger.default};
  font-size: ${({ theme }) => theme.text.paragraph.mdBold.fontSize};
  margin-top: ${Spacing[2]};
`;

export default FormSectionVideoCaptureConfig;
